import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Eye, Edit, Trash2, Filter } from 'lucide-react';
import { postsService } from '../../services/posts';
import { Post, PostFilter, PostAuthorType, PostStatus } from '../../types/post';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const PostsListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<PostFilter>({
    pageSize: 20
  });
  const [showFilters, setShowFilters] = useState(false);

  // Buscar posts
  const { data: postsData, isLoading, error } = useQuery({
    queryKey: ['posts', filters],
    queryFn: () => postsService.getPosts(filters),
  });

  // Deletar post
  const deleteMutation = useMutation({
    mutationFn: (id: string) => postsService.deletePost(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      alert('Post deletado com sucesso!');
    },
    onError: (error: any) => {
      alert(error.response?.data?.message || 'Erro ao deletar post');
    },
  });

  const handleDelete = (id: string) => {
    if (window.confirm('Tem certeza que deseja deletar este post?')) {
      deleteMutation.mutate(id);
    }
  };

  const getStatusBadge = (status: PostStatus) => {
    // Garantir que o status seja um número
    const statusValue = typeof status === 'string' ? parseInt(status, 10) : status;
    
    const statusMap: Record<PostStatus, { label: string; className: string }> = {
      [PostStatus.Draft]: { label: 'Rascunho', className: 'bg-yellow-100 text-yellow-800' },
      [PostStatus.Published]: { label: 'Publicado', className: 'bg-green-100 text-green-800' },
      [PostStatus.Archived]: { label: 'Arquivado', className: 'bg-gray-100 text-gray-800' },
    };
    
    const statusInfo = statusMap[statusValue as PostStatus] || { label: 'Desconhecido', className: 'bg-gray-100 text-gray-800' };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.className}`}>
        {statusInfo.label}
      </span>
    );
  };

  const getAuthorTypeLabel = (authorType: PostAuthorType) => {
    // Garantir que o authorType seja um número
    const authorTypeValue = typeof authorType === 'string' ? parseInt(authorType, 10) : authorType;
    
    const typeMap: Record<PostAuthorType, string> = {
      [PostAuthorType.Place]: 'Cafeteria',
      [PostAuthorType.User]: 'Usuário',
      [PostAuthorType.CoffeeIn]: 'CoffeeIn',
    };
    return typeMap[authorTypeValue as PostAuthorType] || 'Desconhecido';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Erro ao carregar posts: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Posts</h1>
          <p className="text-gray-600">Gerencie os posts do sistema</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filtros
          </button>
          <button
            onClick={() => navigate('/posts/new')}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Post
          </button>
        </div>
      </div>

      {/* Filtros */}
      {showFilters && (
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.status ?? ''}
                onChange={(e) => setFilters({ ...filters, status: e.target.value ? Number(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos</option>
                <option value={PostStatus.Draft}>Rascunho</option>
                <option value={PostStatus.Published}>Publicado</option>
                <option value={PostStatus.Archived}>Arquivado</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Autor
              </label>
              <select
                value={filters.authorType ?? ''}
                onChange={(e) => setFilters({ ...filters, authorType: e.target.value ? Number(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos</option>
                <option value={PostAuthorType.Place}>Cafeteria</option>
                <option value={PostAuthorType.User}>Usuário</option>
                <option value={PostAuthorType.CoffeeIn}>CoffeeIn</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setFilters({ pageSize: 20 })}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Limpar Filtros
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lista de Posts */}
      <div className="bg-white shadow rounded-lg">
        {postsData?.posts.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Nenhum post encontrado.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Título
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Autor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cidade
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Criado em
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {postsData?.posts.map((post: Post) => (
                  <tr key={post.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {post.title}
                      </div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {post.summary}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {post.authorName || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {getAuthorTypeLabel(post.authorType)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(post.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {post.cityName || 'Global'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(post.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => navigate(`/posts/${post.id}`)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Visualizar"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/posts/${post.id}/edit`)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(post.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Deletar"
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Paginação */}
      {postsData?.hasMore && (
        <div className="text-center">
          <button
            onClick={() => setFilters({ ...filters, cursor: postsData.nextCursor })}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Carregar Mais
          </button>
        </div>
      )}
    </div>
  );
};

export default PostsListPage;
